import * as Plot from '@observablehq/plot';
import dayjs from 'dayjs';
import { max, repeat, sum } from 'lodash-es';
import { ChartTooltip } from '../../../components/Chart/components/ChartTooltip';
import { lodashChain } from '../../lodashUtils';
import { generateTimeTicks } from '../timeTicksUtils';
import { pointerX } from '../TooltipMark/pointer';
import { tooltip } from '../TooltipMark/tooltipMark';
import { formatValue, getTextWidth, toTitleCase } from '../utils';
import {
	AXIS_TITLE_TO_TICK_LABEL_PADDING,
	COMMON_PADDING,
	TICK_LABEL_TO_AXIS_PADDING,
} from './constants';
import { TooltipContentRenderer } from './TooltipContentRenderer';
import type { ChartRenderFunction, LegendsInfo } from './types';

export const renderLineChart: ChartRenderFunction = ({
	results: results,
	chartConfig: _chartConfig,
	containerRef,
	chartWidth,
	chartHeight,
	theme,
	chartConfigHelper: chartConfigHelper,
}) => {
	if (results.length === 0) {
		return {
			plot: null,
			legends: {},
		};
	}

	const chartConfig = chartConfigHelper.getAsLineChartConfig();
	const { axes } = chartConfig;
	const xAxisColumn = axes.x[0];
	const yAxisColumns = axes.y;
	const xValues = results.map((result) => result[xAxisColumn]);
	const {
		colors,
		xIsTime,
		forcexAxisRotation,
		showGrid,
		fontSize,
		lineHeight,
		shouldShowXAxisTitle,
		shouldShowYAxisTitle,
		xAxisTitle,
		yAxisTitle,
	} = chartConfigHelper;

	let { xAxisTimeInterval } = chartConfigHelper;

	let xTicksDate: Date[] = [];

	// Format x-axis ticks
	const xTickFormat = (d: number | string | Date) => {
		const columnFormat = chartConfig.columnConfigs?.[xAxisColumn];
		const value = formatValue(d, columnFormat, xAxisTimeInterval);
		return value;
	};

	const yTickFormat = (d: number | string | Date) => {
		if (chartConfig.lineGrouping === 'percentage-stack') {
			return `${d}%`;
		}

		const columnFormat = chartConfig.columnConfigs[yAxisColumns[0]];
		return formatValue(d, columnFormat);
	};

	let xAxisRotation = chartConfigHelper.xAxisTickRotation;
	if (xIsTime) {
		const r = generateTimeTicks({
			// Subtract margin
			chartWidth: chartWidth - 64,
			tickFontSize: fontSize,
			timeValues: xValues,
			interval: xAxisTimeInterval,
			xTickFormat,
		});
		xTicksDate = r.ticks;
		if (xAxisTimeInterval === 'auto') {
			xAxisTimeInterval = r.suggestedInterval;
		}

		if (!forcexAxisRotation) {
			xAxisRotation = r.suggestedRotation;
		}
	}

	const fontFamily = theme.fontFamily || 'inherit';

	if (!xIsTime) {
		const sumLabelsLength = sum(
			xValues.map((d) =>
				getTextWidth(xTickFormat(d) + repeat(' ', 5), fontFamily, fontSize)
			)
		);
		if (sumLabelsLength > chartWidth - 64) {
			if (!forcexAxisRotation) {
				xAxisRotation = 45;
			}
		}
	}

	let xTicksTextWidth =
		max(
			results.map((d) =>
				getTextWidth(xTickFormat(d[xAxisColumn]), fontFamily, fontSize)
			)
		) || 0;

	let xTicksTextHeight = lineHeight;
	if (xAxisRotation >= 45) {
		xTicksTextHeight =
			xTicksTextWidth * Math.sin((xAxisRotation * Math.PI) / 180) +
			lineHeight * Math.sin((xAxisRotation * Math.PI) / 180);
	} else if (xAxisRotation === 0) {
		xTicksTextHeight = lineHeight;
	}

	let yTicksTextWidth =
		max(
			results.map((d) =>
				getTextWidth(
					yTickFormat(d[yAxisColumns[0]]) + '.5',
					fontFamily,
					fontSize
				)
			)
		) || 0;

	const marks: Plot.Markish[] = [];

	const xAxisTitleProperties = {
		label: shouldShowXAxisTitle ? xAxisTitle : null,
		labelOffset: shouldShowXAxisTitle
			? TICK_LABEL_TO_AXIS_PADDING +
				xTicksTextHeight +
				AXIS_TITLE_TO_TICK_LABEL_PADDING
			: undefined,
	};

	const yAxisTitleProperties = {
		label: shouldShowYAxisTitle ? yAxisTitle : null,
		labelOffset: shouldShowYAxisTitle
			? TICK_LABEL_TO_AXIS_PADDING +
				yTicksTextWidth +
				AXIS_TITLE_TO_TICK_LABEL_PADDING
			: undefined,
	};

	// Add grid lines if enabled
	if (showGrid) {
		marks.push(
			Plot.gridY({
				stroke: theme.other.getColor('border/secondary/default'),
				strokeOpacity: 1.0,
			})
		);
	}

	marks.push(
		Plot.frame({
			anchor: 'bottom',
			stroke: theme.other.getColor('border/secondary/default'),
		}),
		Plot.axisX({
			tickSize: 0,
			tickFormat: xTickFormat,
			fontSize: fontSize,
			color: theme.other.getColor('text/secondary/default'),
			tickPadding:
				TICK_LABEL_TO_AXIS_PADDING +
				(xAxisRotation === 0 ? 0 : xTicksTextHeight / 2),
			...xAxisTitleProperties,
			labelArrow: 'none',
			ticks: xIsTime ? xTicksDate : undefined,
			labelAnchor: 'center',
			rotate: -xAxisRotation,
		}),
		Plot.axisY({
			tickFormat: yTickFormat,
			fontSize: fontSize,
			tickSize: 0,
			tickPadding: TICK_LABEL_TO_AXIS_PADDING,
			...yAxisTitleProperties,
			labelArrow: 'none',
			labelAnchor: 'center',
			color: theme.other.getColor('text/secondary/default'),
		})
	);

	const categorySet = new Set<string>();
	const resultsWithCategoryAndFormattedX = lodashChain(results).map((r) => {
		const combinedCategory =
			axes?.category && axes?.category.length > 0
				? axes?.category.map((c) => r[c]).join(' | ')
				: undefined;

		if (combinedCategory) {
			categorySet.add(combinedCategory);
		}

		return {
			...r,
			_category: combinedCategory,
			[xAxisColumn]: xIsTime ? dayjs(r[xAxisColumn]).toDate() : r[xAxisColumn],
		};
	});

	// Need this so that the x axis is in the same order as the data when it's not a time scale
	// (or else Observablehq line chart will always sort the x axis by label)
	const originalOrderXAxisColumn = Array.from(
		new Set(resultsWithCategoryAndFormattedX.value().map((r) => r[xAxisColumn]))
	);

	const resultsWithCategory = resultsWithCategoryAndFormattedX
		.groupBy((r) => {
			let group = r[xAxisColumn];
			if (r._category) {
				group = `${group} | ${r._category}`;
			}
			return group;
		})
		.mapValues((records) => {
			const record = records[0];
			const result = {
				...record,
			};

			for (const yColumn of yAxisColumns) {
				const yValue = records.reduce((acc, record) => {
					if (yColumn in record) {
						acc += record[yColumn];
					}
					return acc;
				}, 0);
				result[yColumn] = yValue;
			}

			return result;
		})
		.values()
		.value();

	const legends: LegendsInfo = {};

	let index = 0;
	for (const yColumn of yAxisColumns) {
		if (categorySet.size > 0) {
			for (const category of [...categorySet]) {
				const legendName = category ? `${yColumn} | ${category}` : yColumn;
				legends[legendName] = {
					color: colors[index % colors.length],
					shortDisplayValue:
						yAxisColumns.length === 1
							? toTitleCase(category)
							: toTitleCase(legendName),
				};
				index += 1;
			}
		} else {
			legends[yColumn] = {
				color: colors[index % colors.length],
				shortDisplayValue: toTitleCase(yColumn),
			};
			index += 1;
		}
	}

	const getColor = (r: Record<string, any>, yColumn: string) => {
		let key = yColumn;
		if (r._category) {
			key = `${yColumn} | ${r._category}`;
		}

		return legends[key].color;
	};

	marks.push(
		Plot.ruleX(
			resultsWithCategory,
			pointerX({
				x: xAxisColumn,
				stroke: theme.other.getColor('border/secondary/default'),
			})
		)
	);

	yAxisColumns.forEach((yColumn) => {
		marks.push(
			Plot.lineY(resultsWithCategory, {
				x: xAxisColumn,
				y: yColumn,
				z: '_category',
				stroke: (d) => getColor(d, yColumn),
				strokeWidth: 2,
			})
		);
		// For dots, each category has to be separate (dots doesn't support z) because Observablehq plot
		// only support showing at most 1 dot at the time, but we want to show all the dots for each category hovering on the same X value
		if (categorySet.size === 0) {
			marks.push(
				Plot.dot(
					resultsWithCategory,
					pointerX({
						x: xAxisColumn,
						y: yColumn,
						fill: (d: Record<string, any>) => getColor(d, yColumn),
						stroke: theme.other.getColor('border/transparent/default'),
						strokeWidth: 2,
						r: 5,
					})
				)
			);
		} else {
			for (const category of [...categorySet]) {
				marks.push(
					Plot.dot(
						resultsWithCategory.filter((d) => d._category === category),
						pointerX({
							x: xAxisColumn,
							y: yColumn,
							fill: (d: Record<string, any>) => getColor(d, yColumn),
							stroke: theme.other.getColor('border/transparent/default'),
							strokeWidth: 2,
							r: 5,
						})
					)
				);
			}
		}
	});

	const legendsAndValuesByGroup = lodashChain(resultsWithCategory)
		.groupBy(xAxisColumn)
		.mapValues((records) => {
			const legendAndValues = [];
			for (const yColumn of yAxisColumns) {
				for (const record of records) {
					let key = yColumn;
					if (record._category) {
						key = `${yColumn} | ${record._category}`;
					}
					if (legends[key]) {
						legendAndValues.push({
							legend: {
								name: key,
								color: legends[key].color,
								shortDisplayValue: legends[key].shortDisplayValue,
							},
							valueFormatted: yTickFormat(record[yColumn]),
						});
					}
				}
			}
			return legendAndValues;
		})
		.value();

	marks.push(
		tooltip(
			resultsWithCategory,
			pointerX({
				x: xAxisColumn,
				tooltipRenderer: ({
					x,
					y,
					dataIndex,
				}: {
					x: number;
					y: number;
					dataIndex: number[];
				}) => {
					const index = dataIndex[0];
					if (index === undefined) {
						return null;
					}
					const record = resultsWithCategory[index];
					const legendAndValuePairs =
						legendsAndValuesByGroup[record[xAxisColumn]];
					const groupValue = xTickFormat(record[xAxisColumn]);
					return (
						<ChartTooltip x={x + 12} y={y}>
							<TooltipContentRenderer
								groupValue={groupValue}
								legendAndValuePairs={legendAndValuePairs}
							/>
						</ChartTooltip>
					);
				},
			})
		)
	);

	const plot = Plot.plot({
		width: chartWidth,
		height: chartHeight,
		marks,
		color: { legend: false },
		marginBottom:
			TICK_LABEL_TO_AXIS_PADDING +
			xTicksTextHeight +
			(shouldShowXAxisTitle ? AXIS_TITLE_TO_TICK_LABEL_PADDING : 0) +
			COMMON_PADDING,
		marginLeft:
			TICK_LABEL_TO_AXIS_PADDING +
			yTicksTextWidth +
			(shouldShowYAxisTitle ? AXIS_TITLE_TO_TICK_LABEL_PADDING : 0) +
			COMMON_PADDING,
		marginTop: COMMON_PADDING * 2,
		marginRight: COMMON_PADDING * 2,
		y: {
			nice: true,
			grid: false, // Disable built-in grid (uses SVG currentColor), use custom grid marks instead
			zero: chartConfigHelper.yAxisStartAtZero,
			percent: chartConfig.lineGrouping === 'percentage-stack',
		},
		x: {
			type: xIsTime ? 'time' : undefined,
			domain: xIsTime ? undefined : originalOrderXAxisColumn,
			grid: false, // Disable built-in grid (uses SVG currentColor), use custom grid marks instead
		},
		style: {
			background: 'transparent',
			fontFamily: theme.fontFamily,
			lineHeight: `50px`,
		},
	});

	containerRef.current?.appendChild(plot);
	return {
		plot,
		legends,
	};
};
